import { useEffect, useMemo, useRef, useState } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import { useCreateScholarshipAwardMutation } from "../../lib/api/scholarships";
import { formatMoney } from "../../lib/format/money";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";

interface ScholarshipFundOption {
  id: string;
  name: string;
  donorId: string | null;
}

interface CreateAwardModalProps {
  open: boolean;
  onClose: () => void;
  students: { id: string; name: string }[];
  funds: ScholarshipFundOption[];
  fundsLoading?: boolean;
  initialStudentId?: string;
}

export function CreateAwardModal({
  open,
  onClose,
  students,
  funds,
  fundsLoading = false,
  initialStudentId,
}: CreateAwardModalProps) {
  const createAward = useCreateScholarshipAwardMutation();
  const { fiscalYearName } = useFiscalDefaults();
  const eligibleFunds = useMemo(() => funds.filter((fund) => fund.donorId), [funds]);
  const [studentId, setStudentId] = useState("");
  const [institution, setInstitution] = useState("");
  const [fundId, setFundId] = useState("");
  const [allocatedAmount, setAllocatedAmount] = useState("");
  const [period, setPeriod] = useState("");
  const [error, setError] = useState<string | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!justOpened) {
      return;
    }

    const preferredStudentId =
      initialStudentId && students.some((student) => student.id === initialStudentId)
        ? initialStudentId
        : (students.at(-1)?.id ?? students[0]?.id ?? "");

    setStudentId(preferredStudentId);
    setFundId(eligibleFunds[0]?.id ?? "");
    setInstitution("");
    setAllocatedAmount("");
    setError(null);
    setPeriod(fiscalYearName ? fiscalYearName.replace(/^FY\s*/, "") : "");
  }, [open, initialStudentId, students, eligibleFunds, fiscalYearName]);

  useEffect(() => {
    if (open && !fundId && eligibleFunds[0]?.id) {
      setFundId(eligibleFunds[0].id);
    }
  }, [open, fundId, eligibleFunds]);

  const parsedAmount = Number.parseFloat(allocatedAmount);
  const canSubmit =
    Boolean(studentId) &&
    institution.trim().length > 0 &&
    Boolean(fundId) &&
    period.trim().length > 0 &&
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    eligibleFunds.length > 0;

  async function submit() {
    setError(null);

    if (!canSubmit) {
      if (eligibleFunds.length === 0) {
        setError(
          "No donor funds are linked to the Scholarship Program. Add a restricted fund under Donors first.",
        );
        return;
      }
      setError("Complete student, institution, donor fund, amount and period.");
      return;
    }

    const amount = parsedAmount.toFixed(2);
    try {
      await createAward.mutateAsync({
        studentId,
        institution: institution.trim(),
        fundId,
        allocatedAmount: amount,
        period: period.trim(),
        allocations: [{ fundId, amount }],
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not create award.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New scholarship award"
      description="Award links a student to a donor fund allocation for the period."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={createAward.isPending || !canSubmit}
            onClick={() => void submit()}
          >
            {createAward.isPending ? "Saving…" : "Create award"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? <p className="edlab-error">{error}</p> : null}
        {fundsLoading ? (
          <p className="text-sm text-edlab-muted">Loading scholarship funds…</p>
        ) : eligibleFunds.length === 0 ? (
          <p className="edlab-notice">
            No donor funds are linked to the Scholarship Program yet. Go to Donors, open a donor,
            and add a restricted fund that includes the Scholarship Program unit.
          </p>
        ) : null}
        <label className="grid gap-1 text-sm">
          Student
          <select
            className="edlab-input"
            value={studentId}
            onChange={(e) => {
              setStudentId(e.target.value);
            }}
            disabled={students.length === 0}
          >
            {students.length === 0 ? (
              <option value="">Add a student first</option>
            ) : (
              students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name}
                </option>
              ))
            )}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Institution
          <input
            className="edlab-input"
            value={institution}
            onChange={(e) => {
              setInstitution(e.target.value);
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Donor fund
          <select
            className="edlab-input"
            value={fundId}
            onChange={(e) => {
              setFundId(e.target.value);
            }}
            disabled={eligibleFunds.length === 0}
          >
            {eligibleFunds.length === 0 ? (
              <option value="">No eligible donor funds</option>
            ) : (
              eligibleFunds.map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))
            )}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Allocated amount
          <input
            type="number"
            min="0"
            step="0.01"
            className="edlab-input"
            value={allocatedAmount}
            onChange={(e) => {
              setAllocatedAmount(e.target.value);
            }}
          />
        </label>
        {allocatedAmount ? (
          <p className="text-sm text-edlab-muted">
            Total allocation: {formatMoney(Number.parseFloat(allocatedAmount) || 0)}
          </p>
        ) : null}
        <label className="grid gap-1 text-sm">
          Period
          <input
            className="edlab-input"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
            }}
          />
        </label>
      </div>
    </Modal>
  );
}
